import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PollDetail } from 'src/lib/hooks/polls/useGetPollDetails';
import {
  GenerateTop5LeaderBoardListModal,
  POLL_ANSWERED_STATE_ENUM,
  PollAttemptedMap,
  PollStateEnum,
  RecordPollDisableFeatureList,
  UserSelfResult,
} from 'src/types/poll.types';

interface PollSliceState {
  isPollInRecordEnabled: boolean;
  recordPollList: PollDetail[];
  pollResult: GenerateTop5LeaderBoardListModal;
  totalPollsConducted: number; // Total number of polls conducted
  userSelfResult: UserSelfResult;
  activePollState: PollStateEnum; // State of the poll in record
  recordPollAttemptedMap: PollAttemptedMap;
  activePollDetails: PollDetail | null;
  pollIdBySecondMap: Record<string, string>; // Maps second to pollId
  pollResultMap: Record<string, GenerateTop5LeaderBoardListModal>;
  studentPollCorrectStatus: POLL_ANSWERED_STATE_ENUM;
  pollSelectedValues: number[]; // Array to hold selected values for the poll
  isPollFeedbackEnabled: boolean; // Flag to enable/disable poll feedback modal
  isPollAttemptedByStudent: boolean; // Flag to indicate if the poll was attempted by the student
  isDisableScreenFeature: boolean; // Flag to disable screen features during poll
  disableFeatureList: RecordPollDisableFeatureList[]; // List of features to disable during the poll
  disableToolTipText: string;
  openPollFeedbackStrip: boolean;
  recordPollAttemptTimer: number;
  isPollFeedbackSubmitted: boolean; // Optional flag to indicate if feedback has been submitted
}

const initialState: PollSliceState = {
  isPollInRecordEnabled: false,
  recordPollList: [],
  pollResult: new GenerateTop5LeaderBoardListModal({ result: {}, leaderboard: [] }),
  totalPollsConducted: 0,
  userSelfResult: {
    totalAttemptPoll: 0,
    totalCorrectPoll: 0,
    averageTime: 0, // milliseconds
    rank: null,
  },
  activePollState: PollStateEnum.NONE,
  recordPollAttemptedMap: {},
  activePollDetails: null,
  pollIdBySecondMap: {},
  pollResultMap: {},
  studentPollCorrectStatus: POLL_ANSWERED_STATE_ENUM.NONE,
  pollSelectedValues: [],
  isPollFeedbackEnabled: false,
  isPollAttemptedByStudent: false, // Initialize with false
  isDisableScreenFeature: false, // Initialize with false
  disableFeatureList: [],
  disableToolTipText: 'All controls are disabled during Poll!',
  openPollFeedbackStrip: false,
  recordPollAttemptTimer: 0,
  isPollFeedbackSubmitted: false,
};

export const pollSlice = createSlice({
  name: 'pollInRecord',
  initialState,
  reducers: {
    updateIsPollInRecordEnabled(state, action: PayloadAction<boolean>) {
      state.isPollInRecordEnabled = action.payload;
    },
    setRecordPollList(state, action: PayloadAction<PollDetail[]>) {
      state.recordPollList = action.payload;
      state.totalPollsConducted = action.payload.length; // Update total polls conducted
    },
    setRecordPollLastResult(state, action: PayloadAction<GenerateTop5LeaderBoardListModal>) {
      state.pollResult = action.payload;
    },
    setPollResultMap(
      state,
      action: PayloadAction<Record<string, GenerateTop5LeaderBoardListModal>>,
    ) {
      state.pollResultMap = action.payload; // Set the map of poll results
    },
    setUserSelfResult(state, action: PayloadAction<UserSelfResult>) {
      state.userSelfResult = action.payload;
    },
    setRecordPollState(state, action: PayloadAction<PollStateEnum>) {
      state.activePollState = action.payload;
    },
    setPollAttempted(state, action: PayloadAction<PollAttemptedMap>) {
      // updating the attempted poll if user already attempted and saved in local storage
      state.recordPollAttemptedMap = action.payload;
    },
    setRecordPollAttemptedMap(
      state,
      action: PayloadAction<{ pollId: string; correctOptions: string; markOptions: string }>,
    ) {
      // updating the attempted poll with pollId when user submit the answer
      const { pollId, correctOptions, markOptions } = action.payload;
      state.recordPollAttemptedMap[pollId] = { correctOptions, markOptions };
    },
    setPollIdBySecondMap(state, action: PayloadAction<Record<string, string>>) {
      state.pollIdBySecondMap = action.payload; // Set the map of seconds to poll IDs
    },
    updateAttemptRecordPollDetails(state, action: PayloadAction<{ pollId: string }>) {
      const pollId = action.payload.pollId;
      state.activePollDetails = state.recordPollList.find((poll) => poll._id === pollId) || null;
      state.activePollState = PollStateEnum.POLL_START; // Set the state to POLL_START when poll details are updated
      // replace result as well
      state.pollResult = new GenerateTop5LeaderBoardListModal({
        result: {},
        leaderboard: state.pollResult?.leaderboard || [],
      });
      state.pollSelectedValues = [];
      state.recordPollAttemptTimer = 0;
    },
    updateActiveRecordPollResult(state, action: PayloadAction<{ isAttempted: boolean }>) {
      // Get pollId from activePollDetails
      const isPollAttempted = action.payload.isAttempted;
      state.isPollAttemptedByStudent = isPollAttempted; // Update the flag for poll attempted by student
      const pollId = state.activePollDetails?._id;
      if (!pollId) return;
      const pollStartTime = state.activePollDetails
        ? state.activePollDetails?.recordPollStartTime.toString()
        : null;
      if (!pollStartTime) return;
      // get result and update pollResult
      if (isPollAttempted) {
        // If attempted, set the entire result (result + leaderboard)
        state.pollResult =
          state.pollResultMap[pollId] ||
          new GenerateTop5LeaderBoardListModal({ result: {}, leaderboard: [] });
      } else {
        // If not attempted, update only the result, keep existing leaderboard
        state.pollResult = new GenerateTop5LeaderBoardListModal({
          result: state.pollResultMap[pollId]?.result || {},
          leaderboard: state.pollResult?.leaderboard || [],
        });
      }
      state.activePollState = PollStateEnum.RESULT; // Update the state to RESULT after fetching the result
      if (isPollAttempted) {
        delete state.pollIdBySecondMap[pollStartTime];
      }
    },
    setStudentPollCorrectStatus(state, action: PayloadAction<POLL_ANSWERED_STATE_ENUM>) {
      state.studentPollCorrectStatus = action.payload; // Set the student's poll correct status
    },
    setPollSelectedValues(state, action: PayloadAction<number[]>) {
      state.pollSelectedValues = action.payload; // Set the selected values for the poll
    },
    resetPollSelectedValues(state) {
      state.pollSelectedValues = []; // Reset the selected values for the poll
    },
    resetActiveRecordPollDetails(state) {
      state.isPollAttemptedByStudent = false;
      state.activePollDetails = null; // Reset active poll details
      state.activePollState = PollStateEnum.NONE; // Reset the poll state
      state.pollSelectedValues = []; // Reset selected values
      state.pollResult = new GenerateTop5LeaderBoardListModal({
        result: {},
        leaderboard: state.pollResult?.leaderboard || [],
      }); // Reset poll result
      state.recordPollAttemptTimer = 0;
    },
    setIsPollFeedbackEnabled(state, action: PayloadAction<boolean>) {
      state.isPollFeedbackEnabled = action.payload;
    },
    handleRecordPollFeatureDisable(state, action: PayloadAction<{ isEnable: boolean }>) {
      const isEnable = action.payload.isEnable;
      state.isDisableScreenFeature = isEnable;
      state.disableFeatureList = isEnable
        ? [RecordPollDisableFeatureList.FOOTER_SECTION, RecordPollDisableFeatureList.THREE_DOT]
        : [];
    },
    setOpenPollFeedbackStrip(state, action: PayloadAction<boolean>) {
      state.openPollFeedbackStrip = action.payload;
    },
    updateRecordPollAttemptTimer(state) {
      state.recordPollAttemptTimer += 1;
    },
    setPollFeedbackSubmitted(state, action: PayloadAction<boolean>) {
      state.isPollFeedbackSubmitted = action.payload;
    },
  },
});

export const {
  updateIsPollInRecordEnabled,
  setRecordPollList,
  setRecordPollLastResult,
  setUserSelfResult,
  setPollAttempted,
  setRecordPollAttemptedMap,
  setPollIdBySecondMap,
  updateAttemptRecordPollDetails,
  updateActiveRecordPollResult,
  setRecordPollState,
  setStudentPollCorrectStatus,
  setPollSelectedValues,
  resetPollSelectedValues,
  setPollResultMap,
  resetActiveRecordPollDetails,
  setIsPollFeedbackEnabled,
  handleRecordPollFeatureDisable,
  setOpenPollFeedbackStrip,
  updateRecordPollAttemptTimer,
  setPollFeedbackSubmitted,
} = pollSlice.actions;

export default pollSlice.reducer;
